import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Document, Repeat } from "assets/icons";
import { UniversalTable, UniversalTableSearch } from 'shared/components/UniversalTable';
import type { UniversalFilterSection, UniversalGroupByOption, CellConfig } from 'shared/components/UniversalTable';
import { Checkbox } from "components/ui/checkbox";
import {
  Chip,
  Tooltip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  Textarea,
} from "@heroui/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import {
  Building,
  EnvelopeSimple,
  FileDashed,
  FileX,
  UsersThree,
  WarningCircle,
} from "@phosphor-icons/react";
import ModalUploadFileLog, {
  FileUploadField,
} from "shared/components/uploadFileModal/uploadFileModal";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  RentalContract,
  RentalGuarantee,
  DocumentStatusType,
  DocumentType,
} from "shared/types/rental-contracts";
import { FileLabel } from "shared/types/file";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { MissingDocumentsReminderModal } from "./missing-documents-reminder-modal";
import { MISSING_DOCUMENTS_REMINDER_TEMPLATES } from "./missing-documents-reminder-templates";

const MISSING_DOCS_COLUMNS = [
  { id: 'checkbox', label: '', minWidth: 40, defaultWeight: 0 },
  { id: 'company', label: 'Vennootschap / gebouw', minWidth: 170, defaultWeight: 1.5 },
  { id: 'unit', label: 'Unit / attribuut', minWidth: 140, defaultWeight: 1, sortKey: 'unit' },
  { id: 'tenant', label: 'Huurder', minWidth: 200, defaultWeight: 2 },
  { id: 'documents', label: 'Ontbrekende documenten', minWidth: 400, defaultWeight: 4 }
];

// Documenten van een gewoon huurcontract (met unit).
const UNIT_DOCUMENT_TYPES = [
  "Ingaande plaatsbeschrijving",
  "Registratiebewijs huurcontract",
  "Huurcontract",
  "Brandverzekering huurder",
  "Huurwaarborg",
];

// Een garage/parking zonder unit wordt anders gepapierd: het garagehuurcontract vervangt
// het woninghuurcontract, met daarnaast de brandverzekering en de waarborg van de garage.
// Plaatsbeschrijving en registratie zijn daar niet van toepassing — de backend vraagt ze ook
// niet op voor zulke contracten. De garagewaarborg is een gewoon documentlabel: de
// "Huurwaarborg" van een woninghuurcontract komt uit `rental_guarantee` (bedrag + type) en
// heeft een eigen invoervenster, wat een garagewaarborg allemaal niet heeft.
const ATTRIBUTE_ONLY_DOCUMENT_TYPES = [
  "Huur garage zonder woninghuurcontract",
  "Brandverzekering garage",
  "Huurwaarborg garage",
];

const MISSING_DOCUMENT_TYPES = [
  ...UNIT_DOCUMENT_TYPES,
  ...ATTRIBUTE_ONLY_DOCUMENT_TYPES,
];

// Label for the "Unit" column. Attribute-only contracts (a standalone garage or
// parking rented without a unit) have no unitId; for those we show the rented
// attribute so the row still says *what* is being rented, next to the building.
export const getRentedObjectLabel = (contract: any, fallback = '—'): string => {
  if (contract?.unitId) return contract.unitId;
  const attributeNames: string[] = contract?.attributeNames || [];
  if (attributeNames.length > 0) return attributeNames.join(', ');
  return contract?.buildingId || fallback;
};

// Single source of truth for the "is the rental guarantee document missing?"
// check. The backend exposes `hasMissingGuarantee`, but we keep the defensive
// fallback (empty array / no fileLogId on the latest guarantee) so the UI stays
// correct even if that flag is ever absent from the payload.
const computeHasMissingGuarantee = (contract: any): boolean => {
  // Voor een garage/parking (contract zonder unit) wordt geen huurwaarborg genomen.
  if (contract?.isAttributeOnly) return false;

  return (
    contract?.hasMissingGuarantee ||
    !contract?.guarantee ||
    contract.guarantee.length === 0 ||
    !contract.guarantee[contract.guarantee.length - 1]?.fileLogId
  );
};

// The upload modal needs a resource to hang the file on: a unit for a normal
// contract, the building for an attribute-only contract (garage/parking). Without
// the building fallback such an upload is refused with "Je moet een gebouw of unit
// selecteren", because those contracts have no unitId at all.
export const getUploadTarget = (contract: any): { unit?: any; building?: any } => {
  if (!contract) return { unit: { id: null } };
  if (contract.unitId) return { unit: { id: contract.unitId } };
  return { building: contract.buildingId ? { id: contract.buildingId } : null };
};

// Which fields the upload modal hides. A garage contract has no unit, so the unit
// picker is noise there; leaving it visible invites filing the document under an
// unrelated unit.
export const getUploadSkipFields = (contract: any): FileUploadField[] =>
  contract && !contract.unitId
    ? [FileUploadField.BUILDING, FileUploadField.UNIT]
    : [FileUploadField.BUILDING];

// Pre-selects the rented garage/parking. That scopes the label list to attribute
// labels (so "Huur garage zonder woninghuurcontract" is offered), files the document
// in the attribute's SharePoint folder, and silences the modal's "attribuut label
// geselecteerd maar geen attribuut gekozen" warning. Only when there is exactly one:
// with several rented attributes we cannot know which one the document belongs to.
export const getUploadAttributeId = (contract: any): number | null => {
  const attributes = contract?.attributes || [];
  return attributes.length === 1 ? attributes[0].id : null;
};

// Extra payload for the upload modal on top of the contract link.
export const getUploadCustomData = (contract: any): Record<string, any> => {
  const attributeId = getUploadAttributeId(contract);
  return attributeId ? { buildingAttributeId: attributeId } : {};
};

const MissingDocumentsSection = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<RentalContract[]>([]);
  const [
    isMissingDocumentUploadModalOpen,
    setIsMissingDocumentUploadModalOpen,
  ] = useState(false);
  const [selectedContract, setSelectedContract] =
    useState<RentalContract | null>(null);
  const [selectedFileLabel, setSelectedFileLabel] = useState(null);
  const [isAddRentalGuaranteeModalOpen, setIsAddRentalGuaranteeModalOpen] =
    useState(false);
  const [filters, setFilters] = useState<string[]>(['ALL']);
  const { session } = useSession();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    contract: any;
    documentLabel: string;
  } | null>(null);
  const [documentStatus, setDocumentStatus] = useState<{
    status: DocumentStatusType;
    reason: string;
  }>({
    status: DocumentStatusType.MISSING_NOT_REQUIRED,
    reason: "",
  });
  const [showDocumentsWithStatus, setShowDocumentsWithStatus] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [buildingFilters, setBuildingFilters] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState('none');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isReminderDocumentsModalOpen, setIsReminderDocumentsModalOpen] =
    useState(false);
  const [selectedReminderDocumentsByContract, setSelectedReminderDocumentsByContract] =
    useState<Record<string, Set<string>>>({});

  const fetchMissingDocuments = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/missing-documents`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch missing documents: ${response.status} ${response.statusText}`,
        );
      }
      const data = await response.json();
      setContracts(data);
    } catch (error) {
      console.error("Failed to fetch missing documents:", error);
      Sentry.captureException(error, {
        tags: { component: "MissingDocumentsSection", action: "fetchMissingDocuments" },
      });
      toast.error("Fout bij het laden van ontbrekende documenten");
    }
  };
  useEffect(() => {
    fetchMissingDocuments();
  }, []);

  // Apply the document-type-independent filters first (building + free-text
  // search). This is the base set used both for the visible rows and for the
  // filter counts, so the badge numbers always reflect the same population the
  // table is showing.
  const searchBuildingFilteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      // Building filter (multi-select on buildingId)
      if (
        buildingFilters.length > 0 &&
        !buildingFilters.includes((contract as any).buildingId)
      ) {
        return false;
      }

      // Search filter — covers tenant name, unit, company, contract ID and missing labels
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const contractHasMissingGuarantee = computeHasMissingGuarantee(contract);

        const matchesSearch = (
          (contract as any).unitId?.toLowerCase().includes(searchLower) ||
          getRentedObjectLabel(contract).toLowerCase().includes(searchLower) ||
          (contract as any).tenantName?.toLowerCase().includes(searchLower) ||
          (contract as any).companyName?.toLowerCase().includes(searchLower) ||
          (contract as any).buildingId?.toLowerCase().includes(searchLower) ||
          (contract as any).contractId?.toString().includes(searchLower) ||
          (contract as any).missingFileLogLabels?.some((label: any) =>
            label.label?.toLowerCase().includes(searchLower)
          ) ||
          (contractHasMissingGuarantee && 'huurwaarborg'.includes(searchLower))
        );
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [contracts, searchText, buildingFilters]);

  const filteredContracts = useMemo(() => {
    // Determine active document types based on filter selection
    const activeDocumentTypes = filters.includes('ALL') ? MISSING_DOCUMENT_TYPES : filters;

    return searchBuildingFilteredContracts.filter((contract) => {
      // A document marked with a status ("niet recupereerbaar") is hidden unless
      // the status filter is active — keep whole-row inclusion in sync with that.
      const documentStatuses = contract.documentStatuses || [];
      const relevantMissingDocs = ((contract as any).missingFileLogLabels || []).filter(
        (fileLabel: any) => {
          const hasStatus = documentStatuses.some(
            status => status.documentType === fileLabel.label
          );
          return !hasStatus || (hasStatus && showDocumentsWithStatus);
        }
      );

      const hasMissingDocuments = relevantMissingDocs.some(
        (fileLabel: any) => activeDocumentTypes.includes(fileLabel.label)
      );

      const hasMissingGuarantee = computeHasMissingGuarantee(contract);

      return hasMissingDocuments ||
        (activeDocumentTypes.includes("Huurwaarborg") && hasMissingGuarantee);
    });
  }, [searchBuildingFilteredContracts, filters, showDocumentsWithStatus]);

  const getReminderDocumentLabels = useCallback((contract: any) => {
    const labels: string[] = (contract.missingFileLogLabels || []).map(
      (fileLabel: any) => fileLabel.label,
    );

    if (
      contract.hasMissingGuarantee &&
      (!contract.guarantee ||
        contract.guarantee.length === 0 ||
        !contract.guarantee[contract.guarantee.length - 1]?.fileLogId)
    ) {
      labels.push('Huurwaarborg');
    }

    return Array.from(new Set(labels));
  }, []);

  const selectedReminderContracts = useMemo(
    () =>
      filteredContracts.filter((contract) =>
        selectedRows.has((contract as any).id),
      ) as any[],
    [filteredContracts, selectedRows],
  );

  const reminderTenants = useMemo(() => {
    return selectedReminderContracts.map((contract) => ({
      id: String(contract.id || contract.contractId),
      name: contract.tenantName || '',
      // Falls back to the rented attribute so a garage row is identifiable in the modal.
      unitId: getRentedObjectLabel(contract, ''),
      documents: MISSING_DOCUMENT_TYPES.filter((label) =>
        getReminderDocumentLabels(contract).includes(label),
      ),
    }));
  }, [selectedReminderContracts, getReminderDocumentLabels]);

  // Honest document counts: only count the documents the table can actually
  // render. We count over the same building/search-filtered population and apply
  // the exact same status visibility rule as the rows, so a badge never claims
  // more documents than are shown. Counts intentionally ignore the document-type
  // selection itself, since the badges ARE that selector.
  const documentCounts = useMemo(() => {
    return MISSING_DOCUMENT_TYPES.reduce(
      (acc, docType) => {
        acc[docType] = searchBuildingFilteredContracts.reduce((count, contract) => {
          if (docType === "Huurwaarborg") {
            return count + (computeHasMissingGuarantee(contract) ? 1 : 0);
          }

          const documentStatuses = contract.documentStatuses || [];
          const isVisible = (contract as any).missingFileLogLabels?.some((label: any) => {
            if (label.label !== docType) return false;
            const hasStatus = documentStatuses.some(
              status => status.documentType === label.label
            );
            return !hasStatus || showDocumentsWithStatus;
          });
          return count + (isVisible ? 1 : 0);
        }, 0);
        return acc;
      },
      {} as { [key: string]: number }
    );
  }, [searchBuildingFilteredContracts, showDocumentsWithStatus]);

  // Number of missing documents that have been marked with a status
  // ("niet recupereerbaar"). These are hidden from the table by default, so we
  // surface the count on the status filter to make them discoverable.
  const nonRecoverableCount = useMemo(() => {
    return searchBuildingFilteredContracts.reduce((count, contract) => {
      const documentStatuses = contract.documentStatuses || [];
      if (documentStatuses.length === 0) return count;
      const withStatus = ((contract as any).missingFileLogLabels || []).filter(
        (label: any) =>
          documentStatuses.some(status => status.documentType === label.label)
      ).length;
      return count + withStatus;
    }, 0);
  }, [searchBuildingFilteredContracts]);

  // Unique building list extracted from the loaded contracts. We pair the
  // building ID with its company name so users can recognise which one to pick
  // when several buildings share a company.
  const buildingOptions = useMemo(() => {
    const seen = new Map<string, string>();
    contracts.forEach((c: any) => {
      if (c.buildingId && !seen.has(c.buildingId)) {
        seen.set(c.buildingId, c.companyName || '');
      }
    });
    return Array.from(seen.entries())
      .map(([id, company]) => ({
        key: id,
        label: company ? `${id} — ${company}` : id,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [contracts]);

  const filterSections: UniversalFilterSection[] = useMemo(() => {
    const totalDocs = Object.values(documentCounts).reduce((sum, count) => sum + count, 0);

    return [
      {
        key: 'documentType',
        label: 'Type document',
        options: [
          { key: 'ALL', label: `Alle documenten (${totalDocs})` },
          ...MISSING_DOCUMENT_TYPES.map(doc => ({
            key: doc,
            label: `${doc} (${documentCounts[doc]})`
          }))
        ],
        color: 'moss' as const,
        multiSelect: true
      },
      {
        key: 'building',
        label: 'Gebouw',
        options: buildingOptions,
        color: 'neutral' as const,
        multiSelect: true,
      },
    ];
  }, [documentCounts, buildingOptions]);

  const groupByOptions: UniversalGroupByOption[] = useMemo(() => [
    { key: 'none', label: 'Geen groepering' }
  ], []);

  const handleDocumentClick = (contract: any, documentLabel: any) => {
    setSelectedContract(contract);
    setSelectedFileLabel(documentLabel);
    if (documentLabel == "Huurwaarborg") {
      setIsAddRentalGuaranteeModalOpen(true);
    } else {
      setIsMissingDocumentUploadModalOpen(true);
    }
  };

  const handleCloseRentalGuaranteeModal = () => {
    setIsAddRentalGuaranteeModalOpen(false);
    setSelectedContract(null);
    setSelectedFileLabel(null);
  };

  const handleCloseFileModal = () => {
    setIsMissingDocumentUploadModalOpen(false);
    setSelectedContract(null);
    setSelectedFileLabel(null);
  };

  const handleAddMissingDocumentLabel = (contract: any, documentLabel: string) => {
    setSelectedDocument({ contract, documentLabel });
    setIsStatusModalOpen(true);
  };

  const handleSubmitStatus = async () => {
    if (!selectedDocument) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/${selectedDocument.contract.contractId}/document-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            documentType: getDocumentType(selectedDocument.documentLabel),
            ...documentStatus,
          }),
        },
        session
      );

      if (response.ok) {
        toast.success("Document status succesvol bijgewerkt");
        fetchMissingDocuments();
        setIsStatusModalOpen(false);
      } else {
        toast.error("Er is iets misgegaan bij het bijwerken van de document status");
      }
    } catch (error) {
      console.error("Error updating document status:", error);
      toast.error("Er is iets misgegaan bij het bijwerken van de document status");
    }
  };

  const handleOpenReminderDocumentsModal = useCallback(() => {
    if (reminderTenants.length === 0) return;

    setSelectedReminderDocumentsByContract(
      Object.fromEntries(
        reminderTenants.map((tenant) => [tenant.id, new Set(tenant.documents)]),
      ),
    );
    setIsReminderDocumentsModalOpen(true);
  }, [reminderTenants]);

  const handleToggleReminderDocument = useCallback((contractId: string, label: string) => {
    setSelectedReminderDocumentsByContract((current) => {
      const selectedDocuments = new Set(current[contractId] || []);
      if (selectedDocuments.has(label)) {
        selectedDocuments.delete(label);
      } else {
        selectedDocuments.add(label);
      }
      return { ...current, [contractId]: selectedDocuments };
    });
  }, []);

  const handleSendReminders = useCallback(async () => {
    const selected = selectedReminderContracts.filter((contract) => {
      const contractId = String(contract.id || contract.contractId);
      return selectedReminderDocumentsByContract[contractId]?.size > 0;
    });
    if (selected.length === 0) return;

    const contacts = selected.map((contract: any) => ({
      id: contract.tenantContactId || contract.tenantResourceId?.toString() || contract.contractId?.toString(),
      name: contract.tenantName || '',
      firstName: contract.tenantFirstName || '',
      lastName: contract.tenantLastName || '',
      email: contract.tenantEmail || '',
      resourceId: contract.tenantContactId || contract.tenantResourceId?.toString() || '',
      units: [],
      buildingId: contract.buildingId || '',
      buildingName: contract.companyName || '',
      unitId: contract.unitId || '',
      // `{unit.number}` in the mail template: for an attribute-only contract the rented
      // garage/parking is the only thing that names what the mail is about.
      unitNumber: getRentedObjectLabel(contract, ''),
    }));

    // Fetch or create a stable RENT-{id} for each selected tenant
    let rentCodeByContactId: Record<string, string> = {};
    try {
      const payload = selected.map((c: any) => ({
        contractId: c.contractId,
        applicationId: c.applicationId ?? null,
        email: c.tenantEmail || '',
        firstName: c.tenantFirstName || '',
        lastName: c.tenantLastName || '',
        unitId: c.unitId || '',
      }));
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/rent-ids`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
        session,
      );
      if (response.ok) {
        const rentIds: Array<{ contractId: number; applicationId: number }> = await response.json();
        const byContractId = Object.fromEntries(rentIds.map((r) => [r.contractId, r.applicationId]));
        rentCodeByContactId = Object.fromEntries(
          selected.map((c: any, idx: number) => {
            const appId = byContractId[c.contractId];
            return [contacts[idx].id, appId ? `RENT-${appId}` : ''];
          })
        );
      }
    } catch (err) {
      console.error('Failed to resolve RENT-ids:', err);
    }

    // Per-contact missing document labels
    const missingDocsPerContact: Record<string, string> = {};
    selected.forEach((contract: any, idx: number) => {
      const id = contacts[idx]?.id;
      if (!id) return;
      const contractId = String(contract.id || contract.contractId);
      const labels = getReminderDocumentLabels(contract).filter((label) =>
        selectedReminderDocumentsByContract[contractId]?.has(label),
      );
      missingDocsPerContact[id] = labels.join(', ');
    });

    navigate('/mass-mail', {
      state: {
        contacts,
        senderEmail: 'rent@yally.be',
        emailSubject: 'Ontbrekende documenten - herinnering',
        contextTemplates: MISSING_DOCUMENTS_REMINDER_TEMPLATES,
        parameters: [
          { id: 'rentCode', name: 'RENT Code', placeholder: '{RentCode}', type: 'individual' },
          { id: 'missingDocs', name: 'Ontbrekende documenten', placeholder: '{OntbrekendeDocumenten}', type: 'individual' },
        ],
        parameterValues: {
          rentCode: rentCodeByContactId,
          missingDocs: missingDocsPerContact,
        },
      },
    });
  }, [
    selectedReminderContracts,
    selectedReminderDocumentsByContract,
    getReminderDocumentLabels,
    navigate,
    session,
  ]);

  const getDocumentType = (label: string): string => {
    switch (label) {
      case 'Huurcontract':
        return DocumentType.RENTAL_CONTRACT;
      case 'Ingaande plaatsbeschrijving':
        return DocumentType.RENTAL_PLACE_DESCRIPTION;
      case 'Huurwaarborg':
        return DocumentType.RENTAL_GUARANTEE;
      case 'Brandverzekering huurder':
        return DocumentType.RENTAL_FIRE_INSURANCE;
      case 'Registratiebewijs huurcontract':
        return DocumentType.RENTAL_REGISTRATION;
      case 'Huur garage zonder woninghuurcontract':
        return DocumentType.GARAGE_RENTAL_CONTRACT;
      case 'Brandverzekering garage':
        return DocumentType.GARAGE_FIRE_INSURANCE;
      case 'Huurwaarborg garage':
        return DocumentType.GARAGE_GUARANTEE;
      default:
        return label;
    }
  };

  const renderRow = useCallback((contract: RentalContract, rowContext?: any): CellConfig[] => {
    const contractAny = contract as any;

    const isDocumentFiltered = (docLabel: string): boolean => {
      if (filters.includes('ALL')) return false;
      return filters.includes(docLabel);
    };

    return [
      // Checkbox column
      {
        id: 'checkbox',
        content: (
          <Checkbox
            checked={rowContext?.isSelected ?? false}
            onCheckedChange={(checked) => rowContext?.onToggleSelect(!!checked)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      // Company column — with the building underneath, so a contract without a
      // unit (garage/parking) still shows where the tenant rents.
      {
        id: 'company',
        content: (
          <div className="flex flex-col">
            <span className="text-sm text-neutral-primary">
              {contractAny.companyName || '—'}
            </span>
            <span className="text-xs text-neutral-secondary-mid">
              {contractAny.buildingId || 'Geen gebouw gekoppeld'}
            </span>
          </div>
        )
      },
      // Unit column
      {
        id: 'unit',
        content: (
          <Chip
            className="gap-1"
            startContent={<Building weight="duotone" size={14} />}
            classNames={{
              base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
              content: "px-0 font-medium",
            }}
          >
            <div className="flex items-center gap-1">
              {getRentedObjectLabel(contractAny)}
              <Tooltip
                content={
                  contractAny.unitId
                    ? `Contract ID: ${contractAny.contractId}`
                    : `Contract ID: ${contractAny.contractId} — verhuur zonder eenheid in gebouw ${contractAny.buildingId || 'onbekend'}`
                }
              >
                <WarningCircle
                  weight="duotone"
                  size={14}
                  className="cursor-pointer"
                />
              </Tooltip>
            </div>
          </Chip>
        )
      },
      // Tenant column
      {
        id: 'tenant',
        content: (
          <Chip
            className="gap-1"
            startContent={<UsersThree weight="duotone" size={14} />}
            classNames={{
              base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
              content: "px-0 font-medium",
            }}
          >
            {contractAny.tenantName}
          </Chip>
        )
      },
      // Documents column
      {
        id: 'documents',
        content: (
          <div className="flex flex-wrap gap-2">
            {contractAny.missingFileLogLabels.map((fileLabel: any, docIndex: number) => {
              const documentStatus = contract.documentStatuses?.find((status: any) =>
                status.documentType === fileLabel.label
              );

              if (documentStatus && !showDocumentsWithStatus) {
                return null;
              }

              return (
                <div className="flex items-center gap-1" key={docIndex}>
                  <Chip
                    className="cursor-pointer gap-1"
                    startContent={<Document className="h-3.5 w-3.5" />}
                    classNames={{
                      base: `h-fit px-2 border ${documentStatus
                        ? documentStatus.status === 'MISSING_REQUIRED'
                          ? 'border-brick-primary/50 bg-brick-primary/10 text-brick-primary'
                          : 'border-moss-primary/50 bg-moss-primary/10 text-moss-primary'
                        : isDocumentFiltered(fileLabel.label)
                          ? "border-brick-primary bg-brick-primary text-white"
                          : "border-warning bg-white text-black"
                      }`,
                      content: "px-0 font-medium",
                    }}
                    onClick={() => handleDocumentClick(contract, fileLabel)}
                  >
                    {fileLabel.label}
                    {documentStatus && (
                      <Tooltip
                        content={`${documentStatus.status === 'MISSING_REQUIRED' ? 'Vereist' : 'Niet vereist'}: ${documentStatus.reason}`}
                        classNames={{
                          content: documentStatus.status === 'MISSING_REQUIRED'
                            ? "bg-brick-primary text-white"
                            : "bg-moss-primary text-white"
                        }}
                      >
                        <span className={`ml-1 text-xs ${documentStatus.status === 'MISSING_REQUIRED'
                          ? 'text-brick-primary'
                          : 'text-moss-primary'
                        }`}>
                          {documentStatus.status === 'MISSING_REQUIRED' ? '⚠' : 'ℹ'}
                        </span>
                      </Tooltip>
                    )}
                  </Chip>
                  {!documentStatus && (
                    <Tooltip content="Markeer document als 'zal nooit meer beschikbaar zijn'">
                      <FileX
                        className="cursor-pointer"
                        onClick={() => handleAddMissingDocumentLabel(contract, fileLabel)}
                        weight="duotone"
                        size={14}
                      />
                    </Tooltip>
                  )}
                </div>
              );
            })}
            {contractAny.hasMissingGuarantee &&
              contract.guarantee &&
              (contract.guarantee.length === 0 ||
                !(contract.guarantee as RentalGuarantee[])[
                  (contract.guarantee as RentalGuarantee[]).length - 1
                ]?.fileLogId) && (
                <div className="flex items-center gap-1">
                  <Chip
                    key="rental-guarantee"
                    className="cursor-pointer gap-1"
                    startContent={<Document className="h-3.5 w-3.5" />}
                    classNames={{
                      base: `h-fit px-2 border ${isDocumentFiltered("Huurwaarborg")
                        ? "border-brick-primary bg-brick-primary text-white"
                        : "border-warning bg-white text-black"
                      }`,
                      content: "px-0 font-medium",
                    }}
                    onClick={() => handleDocumentClick(contract, FileLabel.Huurwaarborg)}
                  >
                    Huurwaarborg
                  </Chip>
                  <Tooltip content="Markeer document als 'zal nooit meer beschikbaar zijn'">
                    <FileX
                      className="cursor-pointer"
                      onClick={() =>
                        handleAddMissingDocumentLabel(contract, FileLabel.Huurwaarborg as any)
                      }
                      weight="duotone"
                      size={14}
                    />
                  </Tooltip>
                </div>
              )}
          </div>
        )
      }
    ];
  }, [filters, showDocumentsWithStatus, handleDocumentClick, handleAddMissingDocumentLabel]);

  return (
    <div id="missing-docs" className="col-span-full flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-lg font-medium">
          <FileDashed weight="duotone" size={20} />
          Ontbrekende documenten
        </div>
        <div className="flex items-center gap-2">
          <a
            className="flex items-center gap-1 rounded-full border border-neutral-primary/20 px-3 py-1.5 text-sm font-medium text-moss-primary"
            href="https://extranet.korfine.net/GuaranteeLease?CombinedStatus=IN_REQUEST&WithDossier=False&AbolishmentInRequest=False&PageSize=10&PageIndex=1"
            target="_blank"
          >
            Korfine
            <ArrowUpRight className="h-[18px] w-[18px]" />
          </a>
          <a
            className="flex items-center gap-1 rounded-full border border-neutral-primary/20 px-3 py-1.5 text-sm font-medium text-moss-primary"
            href="https://financien.belgium.be/nl/e-services/MyRent/Toegang"
            target="_blank"
          >
            My Rent
            <ArrowUpRight className="h-[18px] w-[18px]" />
          </a>
          <ButtonNew
            variant="secondaryNeutral"
            icon={<Repeat />}
            label="Refresh"
            onClick={fetchMissingDocuments}
          />
        </div>
      </header>

      <UniversalTableSearch
        rows={contracts}
        searchValue={searchText}
        onSearchChange={setSearchText}
        filterSections={[
          ...filterSections,
          {
            key: 'documentStatus',
            label: 'Document status',
            options: [
              {
                key: 'showWithStatus',
                label: `Toon niet recupereerbare documenten (${nonRecoverableCount})`,
              }
            ],
            color: 'neutral' as const,
            multiSelect: false
          }
        ]}
        activeFilters={{
          documentType: filters,
          documentStatus: showDocumentsWithStatus ? ['showWithStatus'] : [],
          building: buildingFilters,
        }}
        onFilterChange={(key, values) => {
          setSelectedRows(new Set());
          if (key === 'documentType') {
            if (values.length === 0) {
              setFilters(['ALL']);
            } else if (values.includes('ALL') && values.length > 1) {
              if (!filters.includes('ALL')) {
                setFilters(['ALL']);
              } else {
                setFilters(values.filter(v => v !== 'ALL'));
              }
            } else {
              setFilters(values);
            }
          }
          if (key === 'documentStatus') {
            setShowDocumentsWithStatus(values.includes('showWithStatus'));
          }
          if (key === 'building') {
            setBuildingFilters(values);
          }
        }}
        groupByOptions={groupByOptions}
        selectedGroupBy={groupBy}
        onGroupByChange={setGroupBy}
        selectedCount={selectedRows.size}
        onClearSelection={() => setSelectedRows(new Set())}
        bulkActions={selectedRows.size > 0 ? [
          {
            label: `Herinneringsmail sturen (${selectedRows.size})`,
            icon: <EnvelopeSimple size={16} />,
            onClick: handleOpenReminderDocumentsModal,
            variant: 'primaryMoss',
          }
        ] : []}
      />

      <UniversalTable
        rows={filteredContracts}
        title="Ontbrekende documenten"
        columnsConfig={{ columns: MISSING_DOCS_COLUMNS }}
        groupBy={groupBy}
        renderRow={renderRow}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
      />

      <MissingDocumentsReminderModal
        isOpen={isReminderDocumentsModalOpen}
        onClose={() => setIsReminderDocumentsModalOpen(false)}
        tenants={reminderTenants}
        selectedDocumentsByTenant={selectedReminderDocumentsByContract}
        onToggleDocument={handleToggleReminderDocument}
        onSelectAll={() =>
          setSelectedReminderDocumentsByContract(
            Object.fromEntries(
              reminderTenants.map((tenant) => [
                tenant.id,
                new Set(tenant.documents),
              ]),
            ),
          )
        }
        onClear={() => setSelectedReminderDocumentsByContract({})}
        onConfirm={() => {
          setIsReminderDocumentsModalOpen(false);
          handleSendReminders();
        }}
      />

      {/* Keep all existing modals unchanged */}
      <ModalUploadFileLog
        isOpen={isMissingDocumentUploadModalOpen}
        onClose={handleCloseFileModal}
        onFileUploadSuccess={fetchMissingDocuments}
        {...getUploadTarget(selectedContract)}
        skipFields={getUploadSkipFields(selectedContract)}
        defaultFileLabel={(selectedFileLabel as any)}
        incomingCustomData={{
          rentalContractId: selectedContract && (selectedContract as any).contractId,
          ...getUploadCustomData(selectedContract),
        }}
      />
      <ModalUploadFileLog
        isOpen={isAddRentalGuaranteeModalOpen}
        onClose={handleCloseRentalGuaranteeModal}
        {...getUploadTarget(selectedContract)}
        skipFields={getUploadSkipFields(selectedContract)}
        defaultFileLabel={selectedFileLabel as any}
        incomingCustomData={{
          rentalContractId: selectedContract && selectedContract.id,
          ...getUploadCustomData(selectedContract),
          rentalGuaranteeId:
            selectedContract &&
            selectedContract.guarantee &&
            selectedContract.guarantee.length &&
            selectedContract.guarantee[selectedContract.guarantee.length - 1]
              .id,
        }}
        onFileUploadSuccess={fetchMissingDocuments}
      />
      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        size="lg"
      >
        <ModalContent>
          <ModalHeader>
            Document status bijwerken
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-4">
              <Select
                label="Status"
                selectedKeys={[documentStatus.status]}
                onChange={(e) => setDocumentStatus(prev => ({
                  ...prev,
                  status: e.target.value as DocumentStatusType
                }))}
              >
                <SelectItem key={DocumentStatusType.MISSING_NOT_REQUIRED}>
                  Niet vereist
                </SelectItem>
                <SelectItem key={DocumentStatusType.MISSING_REQUIRED}>
                  Vereist maar niet beschikbaar
                </SelectItem>
              </Select>

              <Textarea
                label="Reden"
                placeholder="Geef een reden waarom dit document niet beschikbaar is..."
                value={documentStatus.reason}
                onChange={(e) => setDocumentStatus(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <ButtonNew
              variant="secondaryNeutral"
              label="Annuleren"
              onClick={() => setIsStatusModalOpen(false)}
            />
            <ButtonNew
              variant="primaryMoss"
              label="Opslaan"
              onClick={handleSubmitStatus}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default MissingDocumentsSection;
